<template>
  <div>
    <div>
      <table class="pe-table mb-4 w-full rounded-lg bg-neutral-50 dark:bg-neutral-800">
        <thead class="bg-neutral-50 dark:bg-neutral-800">
          <tr>
            <th v-if="selectable" class="pe-table--header" width="1">
              <input v-if="selectionType !== 'single'" type="checkbox" v-model="allChecked" />
            </th>
            <th v-for="(col, colKey) in visibleColumns" :key="colKey" class="pe-table--header">
              <div class="flex items-center">
                {{ !col.hideLabel ? col.label : '' }}
                        
                <pe-tooltip v-if="col.showTooltip"   :text=col?.tooltip icon class="ml-2" />     
                
                <span
                  v-if="col.sortable"
                  class="relative ml-2 h-6 cursor-pointer overflow-hidden opacity-50"
                  :title="$t('tooltips.toggleOrder')"
                  @click="changeSorting(col.prop)"
                >
                  <transition
                    mode="out-in"
                    enter-active-class="transition ease-out duration-150 transform"
                    enter-from-class="-translate-y-6"
                    enter-to-class="translate-y-0"
                    leave-active-class="ease-in duration-150"
                    leave-from-class="translate-y-0"
                    leave-to-class="translate-y-6"
                  >
                    <pe-icon v-if="col.prop === sort.prop && sort.asc" icon="keyboard_arrow_down" :size="24" />
                    <pe-icon v-else-if="col.prop === sort.prop" icon="keyboard_arrow_up" :size="24" />
                    <pe-icon v-else icon="unfold_more" :size="24" />
                  </transition>
                </span>

                <pe-dropdown
                  v-if="col.filterable"
                  class="ml-1 text-left"
                  :position="getPosition(colKey)"
                >
                  <pe-icon
                    icon="filter_alt"
                    weight="300"
                    :size="24"
                    :fill="getFilter(col.prop).length > 0"
                    class="cursor-pointer"
                    :class="{
                      'opacity-50': !getFilter(col.prop).length,
                      'text-emerald-300': getFilter(col.prop).length
                    }"
                    @click="toggleFilter(col)"
                  />
                    <template #content>
                      <slot :name="`filter-${col.prop}`">
                        <div>
                          <div class="flex items-center gap-2 px-4 py-2">
                            <input
                              v-model="filterSearch"
                              type="text"
                              class="w-full min-w-[200px] rounded-md border border-solid border-black px-2 text-lg dark:bg-neutral-800
                              dark:text-neutral-100 dark:border-neutral-500"
                              placeholder="Filtrar"
                            />
                            <pe-icon :size="24">search</pe-icon>
                          </div>
                          <div v-if="col.transform === 'active-indicator'">
                            <label
                              v-for="(sts, stsIdx) in [
                                [true, 'sts-ativo', t('labels.active')],
                                [false, 'sts-inativo', t('labels.inactive')]
                              ]"
                              :key="stsIdx"
                              class="flex items-center gap-2 py-2 px-4"
                            >
                              <input type="checkbox" :value="sts[0]" v-model="selectedFilters[col.prop]" />
                              <div
                                class="rounded-full py-[5px] px-4 text-center text-sm font-medium"
                                :class="{
                                  'bg-[#C8F9E5] text-[#0E563A] dark:text-[#17AC6C] dark:bg-[#1C3329]': sts[0],
                                  'bg-[#FDEBF1] text-[#FF4980] dark:bg-[#3F242C]': !sts[0]
                                }"
                              >
                                {{ sts[2] }}
                              </div>
                            </label>
                          </div>
                          <div v-if="filterListItens(defaultFiltersOptions?.[col.prop])?.length > 0 && col.transform !== 'active-indicator'" class="max-h-72 overflow-y-auto font-normal">
                            <label
                              v-for="(filter, fKey) in filterListItens(defaultFiltersOptions?.[col.prop])"
                              :key="fKey"
                              class="block py-3 px-4 text-black transition-all hover:bg-neutral-400/20"
                            >
                              <input type="checkbox" :value="filter" v-model="selectedFilters[col.prop]" />
                              {{ filter }}
                            </label>
                          </div>
                          <div v-if="!filterListItens.length && col.transform !== 'active-indicator'" class="py-2 text-center font-normal">Nenhum resultado encontrado</div>
                          <div class="flex justify-end bg-neutral-500/10 p-4">
                            <pe-button type="primary" @click="resetFilters(col.prop)">{{ t('button.clear') }}</pe-button>
                          </div>
                        </div>
                      </slot>
                    </template>
                </pe-dropdown>
              </div>
            </th>
            <th v-if="actions && actions.length > 0" class="pe-table--header text-center" width="1">{{ t('labels.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loading">
          <tr
            v-for="(row, key) in exibitRows"
            :key="key"
            class="pt-table--row"
            :class="{ 'cursor-pointer': selectable }"
          >
            <td v-if="selectable">
              <input
                v-if="selectionType !== 'single'"
                v-model="checkSelection"
                :value="row[rowIndex]"
                type="checkbox"
              />
              <input v-else v-model="radioSelection" :value="row[rowIndex]" type="radio" :checked="radioSelection === row[rowIndex]"/>
            </td>
            <td v-for="(col, keyCol) in visibleColumns" :key="keyCol" :width="col.width || ''" @click="onClick">
              <slot
                :name="col.prop"
                :row="row"
              >
                <div
                  v-if="col.transform === 'active-indicator'"
                  class="text-center rounded-full py-1 text-sm font-medium"
                  :class="{
                    'bg-[#C8F9E5] text-[#0E563A] dark:text-[#17AC6C] dark:bg-[#1C3329]': row[col.prop],
                    'bg-[#FDEBF1] text-[#FF4980] dark:bg-[#3F242C]': !row[col.prop]
                  }"
                >
                  {{ row[col.prop] ? t('labels.active') : t('labels.inactive') }}
                </div>
                <div v-else-if="col.transform === 'format-boolean'">
                  {{ row[col.prop] ? t('labels.yes') : t('labels.no') }}
                </div>
                <div v-else-if="col.transform === 'format-date'">
                  {{ formatter.date(row[col.prop]) }}
                </div>
                <div v-else-if="col.transform === 'format-cpfcnpj'">
                  {{ formatter.cpfCnpj(row[col.prop]) }}
                </div>
                <div v-else-if="col.transform === 'type-people'">
                  {{ formatter.typePeople(row[col.prop]) }}
                </div>
                <div v-else-if="col.transform === 'type-classificacao-cliente'">
                  {{ formatter.typeClassificacaoCliente(row[col.prop]) }}
                </div>
                <div v-else-if="col.transform === 'status-percentage'">
                  <div
                    class="inline-flex items-center gap-2 rounded-full bg-slate-100 px-2 text-sm font-medium leading-6 dark:bg-white/10 dark:text-neutral-300"
                  >
                    <circle-progress
                      v-if="row[col.prop] > 0"
                      :radius="12"
                      :stroke="3"
                      :progress="row[col.prop]"
                      :segments="10"
                    />
                    <div v-if="row[col.prop] > 0" class="overflow-hidden text-ellipsis">
                      {{ row[col.prop] }}% {{ t('labels.complete') }}
                    </div>
                    <div v-else class="overflow-hidden text-ellipsis px-4">{{ t('labels.notStarted') }}</div>
                  </div>
                </div>
                <div v-else-if="col.transform === 'status'">
                  <div
                    class="inline-flex items-center rounded-full bg-slate-100 px-4 py-2 text-sm font-normal leading-6"
                    :class="{
                      'bg-[#99E2B4] text-black': row[col.prop] === 'Concluído',
                      'bg-[--c-primary-400] text-white': row[col.prop] === 'Em andamento'
                    }"
                  >
                  {{ row[col.prop] }}
                  </div>
                </div>
                <div v-else-if="col.transform === 'to-tag'" class="flex flex-wrap gap-2">
                  <div
                    v-for="(item, index) in row[col.prop]"
                    :key="index"
                    class="inline-block bg-emerald-400 text-xs rounded px-2 py-1 text-white"
                  >
                    {{ item }}
                  </div>
                </div>
                <template v-else-if="typeof col.transform === 'function'">
                  {{ col.transform(row, key) }}
                </template>
                <template v-else-if="col.transform === 'bold'">
                  <div class="font-bold text-blue-700">
                  {{ row[col.prop] }}
                  </div>
                </template>
                <template v-else>
                  {{ row[col.prop] }}
                </template>
              </slot>
            </td>
            <td v-if="row.actions?.length || actions?.length" class="flex items-center justify-start gap-2">
              <slot name="actions" :row="row" />
              <button
                v-for="action in visibleActions"
                :key="action.id"
                :title="row.hintForActions && row.hintForActions[action.id] ? row.hintForActions[action.id] : action.title"
                :disabled="(typeof row.actions?.[action.id] !== 'undefined' && !row.actions[action.id]) || (row.actionsDisabled && row.actionsDisabled?.includes(action.id))"
                class="action-item"
                @click="$emit((action.eventName as any), row)"
              >
                <pe-icon v-if="!action?.newIcon" :size="22" :weight="300" :class="row?.colorAction?.id === action?.id && (row?.colorAction?.color || '')">{{ action.icon }}</pe-icon>
                <UiIcon v-else :icon="action.icon" class="shrink-0" size="24px" stroke-width="1" :class="row?.colorAction?.id === action?.id && (row?.colorAction?.color || '')" />
              </button>
              <button
                v-for="action in defaultActions.filter(el => row.actions?.includes(el.id))"
                :key="action.id"
                :title="action.title"
                class="action-item"
                @click="$emit((action.eventName as any), row)"
              >
                <pe-icon :size="22" :weight="300">{{ action.icon }}</pe-icon>
              </button>
            </td>
          </tr>
        </tbody>
        <tbody v-else class="body__skeleton">
          <tr v-for="(_, index) in pagination.limit" :key="index" class="pt-table--row">
            <td v-if="selectable" width="1"></td>
            <td 
              v-for="(col, keyCol) in visibleColumns" :key="keyCol" :width="col.width || ''"
            >
              <pe-skeleton v-if="['status', 'active', 'ativo'].includes(col.prop)" animated width="24px" height="24px"/>
              <pe-skeleton v-else animated :width="col.width ? `${col.width}px` : ''"/>
            </td>
            <td class="flex items-center justify-end gap-2">
              <pe-skeleton v-for="action in visibleActions" :key="action.id" animated width="24px" height="24px"/>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginação -->
    <div v-if="exibitRows?.length && !loading" class="my-6 flex items-center">
      <span>{{ t('labels.listPerPage') }}</span>
      <select v-model="pagination.limit" class="mx-6 border-0 dark:bg-neutral-800 dark:text-neutral-200">
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
      </select>
      <div>
        {{
          t('labels.pagination', {
            min: (pagination.current - 1) * pagination.limit + 1,
            max: (pagination.current - 1) * pagination.limit + (exibitRows?.length || 0),
            total: pagination.totalRows
          })
        }}
      </div>
      <div v-if="itemsSelected.length" class="ml-5">
        {{ t('labels.selected') }} - {{ itemsSelected.length }}
      </div>
      <div class="flex-grow" />
      <span>{{ t('labels.currentPage', {current: pagination.current, total: pagination.totalPages}) }}</span>
      <button
        class="group mx-1 cursor-pointer border-0 bg-transparent disabled:opacity-50 dark:text-neutral-200"
        :disabled="pagination.current === 1"
        @click="firtstPage()"
      >
        <UiIcon size="23" :icon="'chevrons-left'"
          class="transition-transform"
          :class="{ 'group-hover:-translate-x-1': pagination.current > 1 }" />
      </button>
      <button
        class="group mx-1 cursor-pointer border-0 bg-transparent disabled:opacity-50 dark:text-neutral-200"
        :disabled="pagination.current === 1"
        @click="previousPage()"
      >
        <UiIcon size="20" :icon="'chevron-left'"
          class="transition-transform"
          :class="{ 'group-hover:-translate-x-1': pagination.current > 1 }"/>
      </button>
      <button
        class="group mx-1 cursor-pointer border-0 bg-transparent disabled:opacity-50 dark:text-neutral-200"
        :disabled="pagination.current >= pagination.totalPages"
        @click="nextPage()"
      >
        <UiIcon size="20" :icon="'chevron-right'"
          class="transition-transform"
          :class="{ 'group-hover:translate-x-1': pagination.current < pagination.totalPages }"/>
      </button>
      <button
        class="group mx-1 cursor-pointer border-0 bg-transparent disabled:opacity-50 dark:text-neutral-200"
        :disabled="pagination.current >= pagination.totalPages"
        @click="lastPage()"
      >
        <UiIcon size="23" :icon="'chevrons-right'"
          class="transition-transform"
          :class="{ 'group-hover:translate-x-1': pagination.current < pagination.totalPages }"/>
      </button>
      {{ pagination.current }} - {{ pagination.totalPages }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { strSearch, clamp, formatter } from '@/common/util'
import circleProgress from '@/components/aprendizado/circleProgress.vue'
import UiIcon from '../components/ui/icon.vue'

import type { TableColumnsType } from '@/types/types'

const { t } = useI18n()

const props = defineProps<{
  data: any[]
  actions?: string[]
  columns?: TableColumnsType[]
  searchTerm?: string
  selectable?: boolean
  selectionType?: string
  rowIndex?: string
  defaultSort?: string
  defaultSortOrder?: string
  defaultFilters?: { [key: string]: any[] }
  defaultFiltersOptions?: { [key: string]: any[] }
  loading?: boolean
  pagedInfo?: any
}>()

// @TODO: Isso aqui pode ser melhorado
const defaultActions = ref([
  {
    id: 'editEmail',
    title: t('button.editUserEmail'),
    icon: 'alternate_email',
    eventName: 'onEditEmail'
  },
  {
    id: 'view',
    title: t('button.view'),
    icon: 'eye',
    newIcon: true,
    eventName: 'onView'
  },
  {
    id: 'department',
    title: t('labels.department'),
    icon: 'menu',
    eventName: 'onDepartment'
  },
  {
    id: 'role',
    title: t('labels.jobTitle'),
    icon: 'badge',
    eventName: 'onRole'
  },
  {
    id: 'user',
    title: t('labels.user'),
    icon: 'account_circle',
    eventName: 'onUser'
  },
  {
    id: 'segmentation',
    title: t('labels.segmentation'),
    icon: 'groups',
    eventName: 'onSegmentation'
  },
  {
    id: 'download',
    title: t('button.download'),
    icon: 'download',
    newIcon: true,
    eventName: 'onDownload'
  },
  {
    id: 'associations',
    title: t('button.associations'),
    icon: 'list-check',
    newIcon: true,
    eventName: 'onViewAssociations'
  },
  {
    id: 'sections',
    title: t('labels.associate', {title: t('labels.section')}),
    icon: 'widgets',
    eventName: 'onAssociationsSections'
  },
  {
    id: 'permissions',
    title: t('labels.associate', {title: t('labels.permission')}),
    icon: 'lock_person',
    eventName: 'onAssociationsPermissionGroup'
  },
  {
    id: 'redirect',
    title: t('button.start'),
    icon: 'play_arrow',
    eventName: 'onRedirect'
  },
  {
    id: 'subscriptions',
    title: t('button.studentInscriptions'),
    icon: 'checklist',
    newIcon: true,
    eventName: 'onSubscriptions'
  },
  {
    id: 'qrcode',
    title: t('button.createQrCode'),
    icon: 'qrcode',
    newIcon: true,
    eventName: 'onGenerateQrcode'
  },
  {
    id: 'certificate',
    title: t('button.generateCertificate'),
    icon: 'verified',
    eventName: 'onGenerateCertificate'
  },
  {
    id: 'viewMore',
    title: t('button.seeMore'),
    icon: 'info',
    eventName: 'onViewMore'
  },
  {
    id: 'autoSubscriptions',
    title: t('button.automateJourneys'),
    icon: 'checklist',
    newIcon: true,
    eventName: 'onAutoSubscriptions'
  },
  {
    id: 'associationContracts',
    title: t('labels.associate', {title: t('labels.contract')}),
    icon: 'post_add',
    eventName: 'onAssociationContracts'
  },
  {
    id: 'associationContent',
    title: t('labels.associate', {title: t('labels.data')}),
    icon: 'post_add',
    eventName: 'onAssociationsContent'
  },
  {
    id: 'edit',
    title: t('button.edit'),
    icon: 'edit',
    newIcon: true,
    eventName: 'onEdit'
  },
  {
    id: 'remove',
    title: t('button.delete'),
    icon: 'trash',
    newIcon: true,
    eventName: 'onRemove'
  },
  {
    id: 'analyzeRequest',
    title: t('button.reviewRequest'),
    icon: 'assignment',
    eventName: 'onAnalyzeRequest'
  }
])

const emit = defineEmits([
  'selectionChange',
  'onRemove',
  'onEdit',
  'onView',
  'onDownload',
  'onViewAssociations',
  'onAssociationsSections',
  'onAssociationsPermissionGroup',
  'onRedirect',
  'onSubscriptions',
  'onGenerateQrcode',
  'onGenerateCertificate',
  'onViewMore',
  'onAliareChangePage',
  'onAutoSubscriptions',
  'onAnalyzeRequest',
  'onEditEmail',
  'onAssociationContracts',
  'onAssociationsContent'
])

const checkSelection = ref([])
const radioSelection = ref(null)

const sort = reactive({
  prop: '',
  asc: true
})

const filterSearch = ref('')

watch(
  () => props.searchTerm,
  () => setPage(1)
)

const itemsSelected = computed(() => radioSelection.value ? [radioSelection.value] : checkSelection.value)

const visibleActions = computed(() => defaultActions.value.filter((el) => props.actions?.includes(el.id)))

// Paginação
const pagination = reactive({
  current: 1,
  limit: 10,
  totalRows: computed(() => {
    if (props.pagedInfo != undefined) {
      return props.pagedInfo.totalItems || 0
    }
    return rows.value?.length || 0
  }),
  totalPages: computed(() => {
    if (props.pagedInfo != undefined) {
      return calculateTotalPages(props.pagedInfo.totalItems)
    }
    return calculateTotalPages(rows.value?.length)
  })
}) as { current: number; limit: number; totalRows: number; totalPages: number }


const visibleFilters = reactive([])
const selectedFilters = reactive({})

/**
 * Seta a pagina atual
 * @param page
 */
const setPage = (page: number) => {
  // A páginação só pode ser setada após a atualização das propriedades computadas (nextTick) pois é dependente do calculo do total máximo de paginas
  nextTick(() => {
    pagination.current = clamp(page, 1, pagination.totalPages)
  })
}

/**
 * Calcula o total de páginas
 * @param listLength
 */
const calculateTotalPages = (listLength: number) => {
  return Math.ceil((listLength || 0) / pagination.limit)
}

const onChangePageLimit = () => {
  if (props.pagedInfo != undefined) {
    emit('onAliareChangePage', { page: pagination.current, pageSize: pagination.limit })
  }

  setPage(1)
}

const lastPage = () => {
  if (props.pagedInfo != undefined) {
    emit('onAliareChangePage', { page: pagination.totalPages, pageSize: pagination.limit })
  }

  setPage(pagination.totalPages)
}

const nextPage = () => {
  if (props.pagedInfo != undefined) {
    emit('onAliareChangePage', { page: props.pagedInfo.page + 1, pageSize: pagination.limit })
  }
  
  setPage(pagination.current += 1)
}

const previousPage = () => {
  if (props.pagedInfo != undefined) {
    emit('onAliareChangePage', { page: pagination.current - 1, pageSize: pagination.limit })
  }

  setPage(pagination.current -= 1)
}

const firtstPage = () => {
  if (props.pagedInfo != undefined) {
    emit('onAliareChangePage', { page: 1, pageSize: pagination.limit })
  }

  setPage(1)
}

const getPosition = (colIndex: number): any => {
  const firstIndexes = [0,1].includes(colIndex)
  const lastIndexes = [(visibleColumns.value?.length || 0) - 1].includes(colIndex)
  return firstIndexes ? 'bottom-start' : lastIndexes ? 'bottom-end' : 'bottom' 
}

const visibleColumns = computed(() => {
  return (props.columns?.filter((col) => !col.hidden) || []) as TableColumnsType[]
})

const columnsValuePair = computed(() => props.columns.reduce((acc, el) => (acc[el.prop] = el) && acc, {}))
const currentSelection = ref([])

// Linhas a serem exibidas
const rows = computed(() => {
  let result: any[] = []

  let dataRows = props.data

  const sortProp = sort.prop ? sort.prop : props.defaultSort ? props.defaultSort : ''
  const asc = sort.prop ? sort.asc : props.defaultSortOrder === 'desc' ? false : true

  if (dataRows.length && sortProp !== '') {
    dataRows = dataRows.toSorted((a, b) => (a[sortProp] > b[sortProp] ? 1 : b[sortProp] > a[sortProp] ? -1 : 0) * (asc ? 1 : -1))
  }

  // Executa os filtros internos
  Object.keys(selectedFilters).map((filtro) => {
    if (selectedFilters[filtro]?.length) {
      dataRows = dataRows.filter((row) => selectedFilters[filtro].includes(getFilterFormatter(row, columnsValuePair.value[filtro])))
    }
  })

  if (props.searchTerm) {
    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i]

      const matchCol = props.columns.some((column) => {
        const colValue = row[column.prop]
        if (typeof colValue !== 'string') return false
        return strSearch(colValue, props.searchTerm)
      })

      if (matchCol) result.push(row)
    }
  } else {
    result = dataRows
  }

  if (itemsSelected.value.length) {
    const selected = result.filter((el) => currentSelection.value.includes(el[props.rowIndex]))
    const unselected = result.filter((el) => !currentSelection.value.includes(el[props.rowIndex]))

    result = [...selected, ...unselected]
  }

  return result
})

const exibitRows = computed(() => {
  if (props.pagedInfo != undefined)
    return rows.value

  return rows.value.slice((pagination.current - 1) * pagination.limit, pagination.current * pagination.limit)
})

/**
 * Altera a ordenação da tabela através de uma coluna
 * @param prop
 * @param order Caso não informada segue o padrão (ASC => DESC => Nenhum)
 */
const changeSorting = (prop, order?: boolean) => {
  if (!props.columns.find((el) => el.prop === prop).sortable) return

  // Mesma propriedade ativa
  if (sort.prop === prop) {
    if (typeof order === 'undefined') {
      if (sort.asc === false) {
        sort.prop = ''
      } else {
        sort.asc = !sort.asc
      }
    }
  } else {
    sort.prop = prop
    sort.asc = true
  }
}

const getFormatter = {
  'type-people': formatter.typePeople,
  'format-cpfcnpj': formatter.cpfCnpj,
  'format-date': formatter.date,
  'format-status': (value: boolean) => value ?  t('labels.active') : t('labels.inactive'),
  'active-indicator': (value: boolean) => !!value,
  'format-boolean': (value: boolean) => value ? t('labels.yes') : t('labels.no'),
  'status-percentage': (value: number) => value === 0 ? t('labels.notStarted') : `${value}% ${t('labels.completed')}`,
  'type-classificacao-cliente': formatter.typeClassificacaoCliente,
  'type-produto-aliare': formatter.typeProdutoAliare
}

const getFilterFormatter = (el: any, col: TableColumnsType) => {
  if(!col) return
  return typeof col.transform === 'string' 
    ? getFormatter[col.transform]?.(el[col.prop])
    : typeof col.transform === 'function' 
      ? col.transform(el)
      : el[col.prop]
}

const getFilter = (key: string) => selectedFilters[key] || []

/**
 * Gera a lista de filtros relacionada a coluna selecionada
 * @param prop
 */
const toggleFilter = (col: TableColumnsType) => {
  // Gera a lista unica de dados de filtragem
  visibleFilters.splice(0, visibleFilters.length)
  filterSearch.value = ''

  props.data
    .map((el) => getFilterFormatter(el, col))
    ?.filter((value, index, self) => self.indexOf(value) === index)
    ?.map((el) => visibleFilters.push(el))

  if (typeof selectedFilters[col.prop] === 'undefined') selectedFilters[col.prop] = []
}

const filterListItens = computed(() =>
  (extraData: any[]) =>
    [...new Set([visibleFilters, extraData || []].flat().filter(Boolean))]
      .filter((el) => el && strSearch(String(el), filterSearch.value))
      .sort((a, b) => a.toLowerCase() < b.toLowerCase() ? -1 : 1)
)

/**
 * Reseta o filtro indicado
 * @param prop
 */
const resetFilters = (prop: string) => {
  selectedFilters[prop].splice(0, selectedFilters[prop]?.length || 0)
  filterSearch.value = ''
}

// Listener de mudança de quantidade linhas por página
watch(
  () => pagination.limit,
  () => onChangePageLimit()
)

watch(
  () => ({total: pagination.totalPages, current: pagination.current, filtersLength: Object.keys(selectedFilters).reduce((aggregate, key) => 
    { 
      const size = selectedFilters[key].filter(el => el?.length)?.length || 0
      return aggregate + size
    }, 0)}),
  (newVal, oldVal) => {
    if (newVal.current > newVal.total || newVal.current === 0 || oldVal.filtersLength !== newVal.filtersLength)
      setPage(1)
  },
  {
    deep: true
  }
)

/**
 * Trata o caso de selecionar/desselecionar todos os items
 */
const allChecked = computed({
  get: () => checkSelection.value?.length === rows.value?.length,
  set: (val) => {
    setSelection(val ? rows.value.map((el) => el[props.rowIndex]) : [])
    return val
  }
})

watch(checkSelection, (newVal, oldVal) => {
  if (newVal !== oldVal) emit('selectionChange', checkSelection.value)
})

watch(radioSelection, (newVal, oldVal) => {
  if (newVal !== oldVal) emit('selectionChange', radioSelection.value)
})

const getSelection = () => (props.selectionType !== 'single' ? checkSelection.value : radioSelection.value)

const setSelection = (value: any) => {
  if (props.selectionType !== 'single') {
    checkSelection.value = value
    currentSelection.value = value
  } else {
    radioSelection.value = value
    currentSelection.value = [value]
  }
}

const onClick = (row: Event) => {
  if (!props.selectable) return
  ;(row.target as HTMLElement).closest('tr').querySelector('input')?.click()
}

onMounted(() => {
  if (props.defaultFilters) Object.assign(selectedFilters, props.defaultFilters)
})

defineExpose({
  getSelection,
  setSelection,
  changeSorting,
  setPage,
  nextPage,
  previousPage,
  exibitRows: () => exibitRows.value,
  allRows: () => rows.value, 
  selectedFilters: () => selectedFilters
})
</script>

<style lang="scss" scoped>
table {
  border-spacing: 0;
  background-color: #fff;

  thead {
    background: #fff;
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.1);
    border-radius: 8px;

    tr th {
      &:first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
      }

      &:last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
      }
    }
  }

  tbody {
    tr:not(:first-child) td {
      @apply border-0 border-t border-solid border-t-[#ccc] dark:border-t-neutral-600;
    }

    tr {
      @apply transition-all;
    }
    tr:hover {
      @apply bg-slate-100 dark:bg-neutral-900;
    }
  }

  tbody.body__skeleton {
    tr td {
      border: none;
    }
  }
}

.action-item {
  @apply inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent outline outline-0 outline-offset-0 
  outline-neutral-300/0 transition-all hover:text-[--c-primary-500] focus:text-[--c-primary-500] focus:outline-2 focus:outline-offset-2 
  focus:outline-neutral-300/100 disabled:text-neutral-300 dark:text-neutral-400 dark:hover:text-[--c-primary-600]
  dark:disabled:text-neutral-600;
}

.pe-table--header {
  @apply whitespace-nowrap p-2;
}

.pt-table--row td {
  @apply px-2 py-2;
}

</style>
